import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { SelectPortalContainerContext } from '@/components/ui/select';
import { useIsOfferLocked } from '@/hooks/use-is-offer-locked';
import { useUser } from '@/hooks/use-user';
import { useDealContext } from '@/lib/contexts/deal.context';
import { GenerateSaleExportContext } from '@/lib/contexts/generate-sale-export.context';
import {
  DatasourceInstructionMode,
  ExternalCatalogConnectionCms,
} from '@/lib/graphql/generated/graphql';
import { ProductType } from '@/lib/graphql/generated/graphql';
import { useCreateDealItem } from '@/lib/hooks/useCreateDealItem';
import { useDownloadTemplate } from '@/lib/hooks/useDownloadTemplate';
import { useUpdateDealItems } from '@/lib/hooks/useUpdateDealItems';
import {
  type AudienceOption,
  type BuyOption,
  type CguConfig,
  type CumulOption,
  type PerOrderOption,
  type ShippingOption,
  type SubOption,
  type UsageOption,
  buildCouponTerms as buildCouponTermsUtil,
} from '@/lib/utils/coupon-utils';
import { mapProductImportModeToInstructionMode } from '@/lib/utils/instruction-mode-mapper';
import { pauseCouponCsvRefetchState } from '@/states/pause-coupon-csv-refetch.state';
import { productsState } from '@/states/products.state';
import { PricingModeRequest } from '@/types/pricing-mode-request';
import Button from '@appchoose/button';
import { useAtom, useAtomValue } from 'jotai';
import { FolderInput, Plus } from 'lucide-react';

import { ImportView } from '../import-flow/import-view-props';
import { ViewChooser } from '../import-flow/view-chooser';
import { ViewCouponsCgu } from '../import-flow/view-coupons-cgu';
import { ViewCouponsImportCsv } from '../import-flow/view-coupons-import-csv';
import { ViewCouponsInstructions } from '../import-flow/view-coupons-instructions';
import { ViewCouponsManual } from '../import-flow/view-coupons-manual';
import { ViewCouponsMenu } from '../import-flow/view-coupons-menu';
import { ViewProductsCopyPreviousSales } from '../import-flow/view-products-copy-previous-sales';
import { ViewProductsImportCsvOptionSelection } from '../import-flow/view-products-import-csv-option-selection';
import { ViewProductsManual } from '../import-flow/view-products-manual';
import { ViewProductsMenu } from '../import-flow/view-products-menu';
import { ViewProductsOptionSelection } from '../import-flow/view-products-option-selection';
import { ViewProductsPricingMode } from '../import-flow/view-products-pricing-mode';
import { ViewProductsScraping } from '../import-flow/view-products-scraping';
import { ViewProductsShopify } from '../import-flow/view-products-shopify';
import { ViewInstallShopifyPlugin } from '../import-flow/view-shopify-installation';

type ImportButtonProps = {
  prominent?: boolean;
  generateSaleExport?: boolean;
  defaultOpen?: boolean;
  onClose?: () => void;
  onCloseAutoFocus?: (event: Event) => void;
  children?: React.ReactNode;
};

function ImportButton({
  prominent = false,
  generateSaleExport = false,
  defaultOpen = false,
  onClose,
  onCloseAutoFocus,
  children,
}: ImportButtonProps) {
  const { t } = useTranslation();
  const {
    deal: { id: dealId, ...deal },
    refetchDeal,
  } = useDealContext();
  const user = useUser();
  const isOfferLocked = useIsOfferLocked();
  const location = useLocation();
  const navigate = useNavigate();
  const consumedImportLocation = useRef<string | null>(null);
  const { downloadCouponTemplate } = useDownloadTemplate();
  const products = useAtomValue(productsState);

  // Determine Shopify plugin availability
  const hasShopifyPlugin =
    (deal.externalCatalogConnections?.filter(
      (connection) =>
        connection.cms === ExternalCatalogConnectionCms.Shopify &&
        connection.isActive
    ).length ?? 0) > 0;
  const canHaveShopifyPlugin =
    (deal.externalCatalogConnections?.filter(
      (connection) =>
        connection.cms === ExternalCatalogConnectionCms.Shopify &&
        !connection.isActive
    ).length ?? 0) > 0;

  // States for dialogs
  const [importDialogOpen, setImportDialogOpen] = useState(defaultOpen);
  const [importPortalContainer, setImportPortalContainer] =
    useState<HTMLElement | null>(null);
  const importDialogContentRefCallback = useCallback(
    (node: HTMLElement | null) => setImportPortalContainer(node),
    []
  );
  const [importView, setImportView] = useState<ImportView>(
    generateSaleExport ? 'products' : 'chooser'
  );
  const [instructionMode, setInstructionMode] =
    useState<DatasourceInstructionMode>(DatasourceInstructionMode.CreateOnly);
  const [currentFlow, setCurrentFlow] = useState<
    'scraping' | 'copyPreviousSales' | 'shopify' | null
  >(null);
  const [pricingModeRequest, setPricingModeRequest] =
    useState<PricingModeRequest | null>(null);
  // Vue vers laquelle revenir depuis l'étape « mode de calcul » (l'écran
  // d'origine, en pratique le menu produits).
  const [pricingBackView, setPricingBackView] =
    useState<ImportView>('products');
  // Vue courante en ref, pour mémoriser l'origine au moment où la requête de
  // mode de calcul est déclenchée (le handler est appelé depuis la vue active).
  const importViewRef = useRef(importView);
  importViewRef.current = importView;

  // Affiche le choix du mode de calcul comme ÉTAPE du wizard (même Dialog),
  // au lieu d'une modale séparée → transition fluide depuis le menu produits.
  const handlePricingModeRequested = useCallback(
    (request: PricingModeRequest) => {
      setPricingBackView(importViewRef.current);
      setPricingModeRequest(request);
      setImportView('productsPricingMode');
      setImportDialogOpen(true);
    },
    []
  );

  const handleModifyOption = () => {
    if (currentFlow === 'scraping') {
      setImportView('productsScrapingOptionSelection');
    } else if (currentFlow === 'copyPreviousSales') {
      setImportView('productsCopyPreviousSalesOptionSelection');
    }
  };

  const closeImportDialog = useCallback(() => {
    setImportDialogOpen(false);
    setImportView(generateSaleExport ? 'products' : 'chooser');
    onClose?.();
  }, [generateSaleExport, onClose]);

  useEffect(() => {
    const state: unknown = location.state;
    if (
      !state ||
      typeof state !== 'object' ||
      !('openImport' in state) ||
      state.openImport !== true ||
      user?.isInternalUser !== true ||
      isOfferLocked ||
      !products ||
      consumedImportLocation.current === location.key
    ) {
      return;
    }

    consumedImportLocation.current = location.key;
    setImportView(generateSaleExport ? 'products' : 'chooser');
    setImportDialogOpen(true);
    const { openImport: _openImport, ...remainingState } = state;
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      },
      { replace: true, state: remainingState }
    );
  }, [
    location,
    generateSaleExport,
    navigate,
    user?.isInternalUser,
    isOfferLocked,
    products,
  ]);

  const { createItem: createCoupon, loading: creatingCoupon } =
    useCreateDealItem(dealId, generateSaleExport);
  const { updateProducts } = useUpdateDealItems(dealId);
  const [importedCouponGtins, setImportedCouponGtins] = useState<string[]>([]);
  const [fromCsvImport, setFromCsvImport] = useState(false);
  const [pendingCouponUpdate, setPendingCouponUpdate] = useState<{
    gtins: string[];
    couponTerms: string[];
    couponHowToUse: string[];
  } | null>(null);
  const [, setPauseCouponRefetch] = useAtom(pauseCouponCsvRefetchState);
  const [couponForm, setCouponForm] = useState<{
    name: string;
    gtin: string;
    listPrice: string;
    choosePrice: string;
    description: string;
    expiration: string;
    experienceDeadline: string;
  }>({
    name: '',
    gtin: '',
    listPrice: '',
    choosePrice: '',
    description: '',
    expiration: '',
    experienceDeadline: '',
  });
  const canCreateCoupon = Boolean(
    couponForm.name.trim() &&
    couponForm.gtin.trim() &&
    couponForm.listPrice.trim() &&
    couponForm.choosePrice.trim() &&
    couponForm.description.trim() &&
    couponForm.expiration.trim() &&
    couponForm.experienceDeadline.trim()
  );

  const [couponTermsTemplate, setCouponTermsTemplate] = useState<string[]>([]);
  const [howToUseTemplate, setHowToUseTemplate] = useState<string[]>([]);
  const [selectedFlow, setSelectedFlow] = useState<
    'simple' | 'service' | 'custom' | null
  >(null);

  const [cgu, setCgu] = useState<CguConfig>({
    audience: {
      include: true,
      option: undefined as AudienceOption | undefined,
    },
    usage: { include: true, option: undefined as UsageOption | undefined },
    buy: { include: true, option: undefined as BuyOption | undefined },
    perOrder: {
      include: true,
      option: undefined as PerOrderOption | undefined,
    },
    cumul: { include: true, option: undefined as CumulOption | undefined },
    subscription: { include: true, option: undefined as SubOption | undefined },
    shipping: {
      include: true,
      option: undefined as ShippingOption | undefined,
    },
    extra: '',
  });

  // Automatically update products when they appe after CSV import
  useEffect(() => {
    if (!pendingCouponUpdate || pendingCouponUpdate.gtins.length === 0) {
      return;
    }

    const toUpdate = products.filter((p) =>
      pendingCouponUpdate.gtins.includes(p.activeVersion.gtin ?? '')
    );

    if (toUpdate.length > 0) {
      updateProducts(
        toUpdate.map((p) => ({
          id: p.activeVersion.id,
          productType: ProductType.Coupon,
          couponTerms: pendingCouponUpdate.couponTerms,
          couponHowToUse: pendingCouponUpdate.couponHowToUse,
        }))
      ).then(() => {
        setPendingCouponUpdate(null);
      });
    }
  }, [products, pendingCouponUpdate, updateProducts]);

  const buildCouponTerms = (): string[] => {
    return buildCouponTermsUtil({
      cgu,
      experienceDeadline: couponForm.experienceDeadline,
    });
  };
  if (isOfferLocked || !products) {
    return null;
  }

  return (
    <GenerateSaleExportContext.Provider value={generateSaleExport}>
      <Dialog
        open={importDialogOpen}
        onOpenChange={(open) => {
          setImportDialogOpen(open);
          if (!open) {
            // Fermeture pendant l'étape « mode de calcul » → on résout la
            // requête en attente comme une annulation (évite une promesse
            // pendante dans CsvBoxImportButton).
            pricingModeRequest?.onDecision(null);
            setPricingModeRequest(null);
            closeImportDialog();
          }
        }}
      >
        {children !== null && (
          <DialogTrigger asChild>
            {children ?? (
              <Button
                type="button"
                data-user-guide="Importer"
                appearance={prominent ? 'primary' : 'outline'}
                data-testid="import-button"
                className={
                  prominent
                    ? 'flex items-center gap-2 shadow-sm'
                    : 'flex items-center gap-2 border border-gray-300 font-medium'
                }
                disabled={isOfferLocked}
                // Toujours repartir du menu : sinon une vue d'un flow précédent
                // (ex. étape « mode de calcul ») resterait affichée à la réouverture.
                onClick={() =>
                  setImportView(generateSaleExport ? 'products' : 'chooser')
                }
              >
                {prominent ? (
                  <Plus className="h-4 w-4" strokeWidth={2.5} />
                ) : (
                  <FolderInput className="h-4 w-4" />
                )}
                {t(prominent ? 'general.add_products' : 'general.import')}
              </Button>
            )}
          </DialogTrigger>
        )}
        <DialogContent
          onCloseAutoFocus={onCloseAutoFocus}
          ref={importDialogContentRefCallback}
          className="flex max-h-[85vh] min-h-0 flex-col sm:max-w-[700px]"
        >
          <SelectPortalContainerContext.Provider value={importPortalContainer}>
            {importView === 'installShopifyPlugin' && deal.brandId ? (
              <ViewInstallShopifyPlugin
                setImportView={setImportView}
                brandId={deal.brandId}
                instructionMode={instructionMode}
                refetchDeal={refetchDeal}
                dealId={dealId}
                onModifyOption={handleModifyOption}
                onCloseDialog={closeImportDialog}
              />
            ) : importView === 'chooser' ? (
              <ViewChooser setImportView={setImportView} />
            ) : importView === 'products' ? (
              <ViewProductsMenu
                setImportView={setImportView}
                isInternalUser={user?.isInternalUser ?? false}
                isDeveloper={user?.isDeveloper ?? false}
                products={products}
                dealId={dealId}
                onCloseDialog={closeImportDialog}
                hasShopifyPlugin={hasShopifyPlugin}
                canHaveShopifyPlugin={canHaveShopifyPlugin}
                onPricingModeRequested={handlePricingModeRequested}
              />
            ) : importView === 'productsImportCSVOptionSelection' ? (
              <ViewProductsImportCsvOptionSelection
                flowTitle={t('general.import_csv_file')}
                dealId={dealId}
                onBack={() => setImportView('products')}
                onUploadSuccess={async () => {
                  closeImportDialog();
                }}
                onCloseDialog={closeImportDialog}
                setImportView={setImportView}
                products={products}
                onPricingModeRequested={handlePricingModeRequested}
              />
            ) : importView === 'productsManual' ? (
              <ViewProductsManual
                setImportView={setImportView}
                dealId={dealId}
                isInternalUser={user?.isInternalUser ?? false}
                onCloseDialog={closeImportDialog}
              />
            ) : importView === 'productsCopyPreviousSales' ? (
              <ViewProductsCopyPreviousSales
                setImportView={setImportView}
                dealId={dealId}
                instructionMode={instructionMode}
                onModifyOption={handleModifyOption}
                onCloseDialog={closeImportDialog}
                cameFromOptions={currentFlow === 'copyPreviousSales'}
                products={products}
              />
            ) : importView === 'productsScraping' ? (
              <ViewProductsScraping
                setImportView={setImportView}
                dealId={dealId}
                instructionMode={instructionMode}
                onModifyOption={handleModifyOption}
                onCloseDialog={closeImportDialog}
                cameFromOptions={currentFlow === 'scraping'}
              />
            ) : importView === 'productsShopify' ? (
              <ViewProductsShopify
                setImportView={setImportView}
                dealId={dealId}
                instructionMode={instructionMode}
                onModifyOption={handleModifyOption}
                onCloseDialog={closeImportDialog}
              />
            ) : importView === 'productsShopifyOptionSelection' ? (
              <ViewProductsOptionSelection
                flowTitle={t(
                  'components.import_flow.products_menu.shopify_import'
                )}
                onNext={(mode) => {
                  const mappedMode =
                    mapProductImportModeToInstructionMode(mode);
                  setInstructionMode(mappedMode);
                  setCurrentFlow('shopify');
                  setImportView('productsShopify');
                }}
                onBack={() => setImportView('products')}
                setImportView={setImportView}
                products={products}
              />
            ) : importView === 'productsScrapingOptionSelection' ? (
              <ViewProductsOptionSelection
                flowTitle={t(
                  'components.import_flow.products_menu.scrape_website'
                )}
                onNext={(mode) => {
                  const mappedMode =
                    mapProductImportModeToInstructionMode(mode);
                  setInstructionMode(mappedMode);
                  setCurrentFlow('scraping');
                  setImportView('productsScraping');
                }}
                onBack={() => setImportView('products')}
                setImportView={setImportView}
                products={products}
              />
            ) : importView === 'productsCopyPreviousSalesOptionSelection' ? (
              <ViewProductsOptionSelection
                flowTitle={t(
                  'components.import_flow.copy_previous_sales.title'
                )}
                onNext={(mode) => {
                  const mappedMode =
                    mapProductImportModeToInstructionMode(mode);
                  setInstructionMode(mappedMode);
                  setCurrentFlow('copyPreviousSales');
                  setImportView('productsCopyPreviousSales');
                }}
                onBack={() => setImportView('products')}
                setImportView={setImportView}
                products={products}
              />
            ) : importView === 'productsPricingMode' ? (
              <ViewProductsPricingMode
                request={pricingModeRequest}
                isInternalUser={user?.isInternalUser ?? false}
                onConfirm={() => setPricingModeRequest(null)}
                onBack={() => {
                  setPricingModeRequest(null);
                  setImportView(pricingBackView);
                }}
              />
            ) : importView === 'coupons' ? (
              <ViewCouponsMenu setImportView={setImportView} />
            ) : importView === 'couponsImport' ? (
              <ViewCouponsImportCsv
                setImportView={setImportView}
                dealId={dealId}
                downloadTemplate={downloadCouponTemplate}
                setImportedCouponGtins={setImportedCouponGtins}
                setFromCsvImport={setFromCsvImport}
                setPauseCouponRefetch={setPauseCouponRefetch}
                products={products}
              />
            ) : importView === 'couponsManual' ? (
              <ViewCouponsManual
                setImportView={setImportView}
                couponForm={couponForm}
                setCouponForm={setCouponForm}
                canCreateCoupon={canCreateCoupon}
                creatingCoupon={creatingCoupon}
              />
            ) : importView === 'couponsCGU' ? (
              <ViewCouponsCgu
                setImportView={setImportView}
                cgu={cgu}
                setCgu={setCgu}
                buildCouponTerms={buildCouponTerms}
                setCouponTermsTemplate={setCouponTermsTemplate}
              />
            ) : [
                'instructionsSelect',
                'instructionsBuild',
                'instructionsPreview',
              ].includes(importView) ? (
              <ViewCouponsInstructions
                setImportView={setImportView}
                selectedFlow={selectedFlow}
                setSelectedFlow={setSelectedFlow}
                howToUseTemplate={howToUseTemplate}
                setHowToUseTemplate={setHowToUseTemplate}
                couponForm={couponForm}
                onFinish={async () => {
                  if (fromCsvImport) {
                    setPendingCouponUpdate({
                      gtins: importedCouponGtins,
                      couponTerms: couponTermsTemplate,
                      couponHowToUse: howToUseTemplate,
                    });
                  } else {
                    if (!canCreateCoupon) return;
                    await createCoupon({
                      name: couponForm.name,
                      gtin: couponForm.gtin,
                      listPrice: parseFloat(
                        couponForm.listPrice.replace(',', '.')
                      ),
                      choosePrice: parseFloat(
                        couponForm.choosePrice.replace(',', '.')
                      ),
                      description: couponForm.description,
                      productType: ProductType.Coupon,
                      shippingPrice: 0,
                      couponExpirationDate: new Date(couponForm.expiration),
                      couponTerms: couponTermsTemplate,
                      couponHowToUse: howToUseTemplate,
                      isSelected: true,
                    });
                  }
                  setFromCsvImport(false);
                  setPauseCouponRefetch(false);
                  setImportedCouponGtins([]);
                  closeImportDialog();
                }}
                finishing={creatingCoupon}
                importView={importView}
              />
            ) : (
              <></>
            )}
          </SelectPortalContainerContext.Provider>
        </DialogContent>
      </Dialog>
    </GenerateSaleExportContext.Provider>
  );
}

export default ImportButton;
